import * as React from "react";
import { FormattedMessage } from "react-intl";
import { Modal } from "react-bootstrap";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { getBackend } from "@/lib/backend/backend2";
import type { IModelBaseInfo } from "@/lib/backend/models.generated.types";
import type { ILineGroupData, IMaterialModel, IPlantData } from "@/lib/backend/manufacturing2.generated.types";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import Select from "react-select";
import { createXlsxAndDownload, type ILabelValuePair } from "@/lib/util";
import local_settings, { FIELD_USER_PLANTS } from "@/lib/localSettings";

type Props = {
    show: boolean;
    modelsInfo: IModelBaseInfo[];
    onHide: () => void;
};

type State = {
    is_loading: boolean;
    error: string;
    selectedLineGroup: ILabelValuePair | null;
    selectedPlant: ILabelValuePair | null;
    selectedMaterial: ILabelValuePair | null;
    line_groups: ILineGroupData[];
    plants: IPlantData[];
    models: IMaterialModel[];
};

class ExportLineGroupMaterialsModal extends React.Component<Props, State> {
    state: State = {
        is_loading: true,
        error: "",
        selectedLineGroup: null,
        selectedPlant: null,
        selectedMaterial: null,
        line_groups: [],
        plants: [],
        models: [],
    };

    async componentDidMount() {
        try {
            const { plants } = await getBackend().manufacturing.getPlants({});
            const user_plants = (local_settings.getItem(FIELD_USER_PLANTS) || "").split(",");
            const filtered_plants = plants.filter(plant => user_plants.includes(plant.uuid));

            const { line_groups } = await getBackend().manufacturing.getLineGroups({});

            let only_plant: ILabelValuePair | null = null;

            if (filtered_plants.length === 1) {
                only_plant = {
                    value: filtered_plants[0].uuid,
                    label: filtered_plants[0].title,
                };
            }

            this.setState({
                error: "",
                is_loading: false,
                line_groups,
                plants: filtered_plants,
                selectedPlant: only_plant,
            });
        } catch (error) {
            this.setState({ error: (error as Error).message, is_loading: false });
        }
    }

    closeModal = () => {
        // Do not reset selectedPlant if there is only one plant
        let selectedPlant = this.state.selectedPlant;
        if (this.state.plants.length > 1) {
            selectedPlant = null;
        }
        // Reset selected
        this.setState({ selectedLineGroup: null, selectedMaterial: null, selectedPlant }, () => this.props.onHide());
    };

    getSelectLineGroupOptions = (): ILabelValuePair[] => {
        const matched_line_groups: string[] = [];

        if (this.state.selectedPlant) {
            for (const line_group of this.state.line_groups) {
                const match = line_group.plant_uuids.includes(this.state.selectedPlant.value);

                if (match) {
                    matched_line_groups.push(line_group.uuid);
                }
            }
        }

        return this.state.line_groups
            .filter(line_group => (this.state.selectedPlant ? matched_line_groups.includes(line_group.uuid) : true))
            .map(line_group => {
                return {
                    value: line_group.uuid,
                    label: line_group.title,
                };
            });
    };

    getSelectPlantsOptions = (): ILabelValuePair[] => {
        return this.state.plants.map(plant => {
            return {
                value: plant.uuid,
                label: plant.title,
            };
        });
    };

    getSelectMaterialsOptions = (): ILabelValuePair[] => {
        return this.state.models.map(model => {
            return {
                value: model.uuid,
                label: model.title,
            };
        });
    };

    onLineGroupChange = (selected: { value: string; label: string }) => {
        this.setState({ selectedLineGroup: selected, error: "" });
    };

    onPlantChange = (selected: { value: string; label: string }) => {
        this.setState({ selectedPlant: selected, error: "", selectedLineGroup: null });
    };

    onMaterialChange = (selected: { value: string; label: string }) => {
        this.setState({ selectedMaterial: selected, error: "" });
    };

    /**
     * The methods below remove accents from string and replace spaces with dashes
     * to create a valid file name. Max length of file name is 20 characters.
     *
     */
    removeAccents = (str: string) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    createFileName = (str: string) =>
        this.removeAccents(str.replace(/\s/g, "-").toLowerCase())
            .replace(/[^a-z-A-Z0-9\s]/g, "")
            .replace(/-{2,}/g, "-")
            .substring(0, 19);

    saveLineGroup = async () => {
        try {
            this.setState({ is_loading: true });
            const backend = getBackend();

            let params: Record<string, string | string[]> = {};
            if (
                this.state.selectedLineGroup !== undefined &&
                this.state.selectedLineGroup !== null &&
                this.state.selectedLineGroup.value !== ""
            ) {
                params = {
                    linegroup_uuid: this.state.selectedLineGroup && this.state.selectedLineGroup.value,
                };
            } else if (
                this.state.selectedMaterial !== undefined &&
                this.state.selectedMaterial !== null &&
                this.state.selectedMaterial.value !== ""
            ) {
                params = {
                    material_external_ids: [this.state.selectedMaterial && this.state.selectedMaterial.value],
                };
            }

            if (Object.keys(params).length === 0) {
                this.setState({
                    error: translate("common.filters_selected_none", "No filters selected"),
                    is_loading: false,
                });
                return;
            }

            const { report } = await backend.models.getReport(params);

            const sorted_report = report.map(rep => ({
                material_number: rep.material_number,
                line_external_id: rep.line_external_id,
                process_number: rep.process_num,
                normative: rep.normative,
                model: rep.model,
                diff: rep.diff,
                model_normative_diff_percent: rep.model_normative_diff_percent,
            }));

            const filename = `report_${this.createFileName((this.state.selectedLineGroup && this.state.selectedLineGroup.label) || "")}.xlsx`;

            createXlsxAndDownload(JSON.stringify(sorted_report), filename);

            this.closeModal();
            this.setState({ is_loading: false });
        } catch (error) {
            this.setState({ error: (error as Error).toString(), is_loading: false });
        }
    };

    render() {
        const lineGroupOptions = this.getSelectLineGroupOptions();
        const plantOptions = this.getSelectPlantsOptions();

        return (
            <Modal show={this.props.show} onHide={this.closeModal} backdrop="static">
                <Modal.Header>
                    <Modal.Title as={"div"}>
                        <h5>{translate("common.select_line_group", "Select line group")}</h5>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!this.state.is_loading && (
                        <Select
                            options={plantOptions}
                            value={this.state.selectedPlant}
                            placeholder={translate("common.select_plant", "Select plant")}
                            onChange={this.onPlantChange}
                            isClearable={this.state.plants.length > 1}
                            className="mb-3"
                        />
                    )}
                    {!this.state.is_loading && (
                        <Select
                            options={lineGroupOptions}
                            value={this.state.selectedLineGroup}
                            placeholder={translate("common.select_line_group", "Select line group")}
                            onChange={this.onLineGroupChange}
                            isDisabled={!this.state.selectedPlant}
                            isClearable
                            className="mb-3"
                        />
                    )}
                    {/*
                        Uncomment this when BE supports filtering by material
                     */}
                    {/* {!this.state.is_loading && <Select
                        options={materialsOptions}
                        value={this.state.selectedMaterial}
                        placeholder={translate("common.select_line_group1", "Select material")}
                        onChange={this.onMaterialChange}
                        isDisabled={this.state.selectedLineGroup}
                        isClearable
                    />} */}
                    {this.state.is_loading && (
                        <div className="loader_box">
                            <div className="loader">Loading...</div>
                        </div>
                    )}
                    {this.state.error !== "" ? <ErrorComponent msg={this.state.error} type="error" /> : null}
                </Modal.Body>
                <Modal.Footer>
                    <button
                        onClick={this.closeModal}
                        className="btn btn-outline-secondary"
                        disabled={this.state.is_loading}
                    >
                        <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                    </button>
                    <button onClick={this.saveLineGroup} className="btn btn-primary" disabled={this.state.is_loading}>
                        <FormattedMessage id="common.save" defaultMessage="Save" />
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default ExportLineGroupMaterialsModal;
